import type cytoscape from 'cytoscape';

/**
 * Die sieben festen Zoomstufen von Planung und Bibliothek. Die erste ist der weiteste Blick (min), die letzte die größte
 * Vergrößerung (max); dazwischen gibt es nichts. Alles auf der Fläche wird mit dem Zoom gleichmäßig größer bzw. kleiner; die
 * Anzeige Fern/Mittel/Nah teilt die Stufen grob ein (Fern < 0,45 ≤ Mittel < 1,3 ≤ Nah: zwei, drei und zwei Stufen).
 */
export const ZOOM_LEVELS = [0.15, 0.25, 0.45, 0.65, 0.95, 1.4, 2.0] as const;
export const MIN_ZOOM = ZOOM_LEVELS[0];
export const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!;

/** Nächstliegende Stufe (nach Verhältnis, nicht nach Differenz). */
export function nearestLevel(zoom: number): number {
  let best: number = ZOOM_LEVELS[0];
  for (const l of ZOOM_LEVELS) if (Math.abs(Math.log(l / zoom)) < Math.abs(Math.log(best / zoom))) best = l;
  return best;
}

/** Die nächste Stufe über (`+1`) bzw. unter (`-1`) dem Zoom; an den Enden bleibt es bei der äußersten Stufe. */
export function stepLevel(zoom: number, direction: 1 | -1): number {
  const eps = 1e-6;
  if (direction === 1) return ZOOM_LEVELS.find((l) => l > zoom + eps) ?? MAX_ZOOM;
  return [...ZOOM_LEVELS].reverse().find((l) => l < zoom - eps) ?? MIN_ZOOM;
}

/** Größte Stufe, die höchstens `required` beträgt (damit etwas ganz sichtbar ist); die kleinste Stufe, wenn selbst die zu groß ist. */
export function levelFitting(required: number): number {
  return [...ZOOM_LEVELS].reverse().find((l) => l <= required + 1e-9) ?? MIN_ZOOM;
}

/**
 * Übersetzt Mausrad-Ereignisse in Stufenwechsel: eine Rastung (ab 100 Einheiten) ist sofort eine Stufe; kleine Werte (Touchpad,
 * fein auflösende Räder) werden gesammelt, ab genug Weg gibt es eine Stufe, danach eine kurze Pause. Liefert `+1` (hinein),
 * `-1` (heraus) oder `0`.
 */
export function createWheelStepper(now: () => number = () => performance.now()): (deltaY: number, deltaMode: number) => 1 | -1 | 0 {
  let acc = 0;
  let last = Number.NEGATIVE_INFINITY;
  return (deltaY, deltaMode) => {
    const px = deltaMode === 1 ? deltaY * 33 : deltaMode === 2 ? deltaY * 800 : deltaY;
    if (px === 0) return 0;
    if (Math.abs(px) >= 100) {
      acc = 0;
      last = now();
      return px < 0 ? 1 : -1;
    }
    if (Math.sign(px) !== Math.sign(acc)) acc = 0;
    acc += px;
    if (Math.abs(acc) < 60 || now() - last < 150) return 0;
    const dir = acc < 0 ? 1 : -1;
    acc = 0;
    last = now();
    return dir;
  };
}

/** Ziel der laufenden Zoom-Animation je Canvas, damit schnell aufeinanderfolgende Rastungen Stufe für Stufe weiterzählen. */
const targets = new WeakMap<cytoscape.Core, number>();

/** Eine Stufe hinein oder heraus, um die Mausposition (Bildschirmkoordinaten im Canvas). */
export function zoomStep(cy: cytoscape.Core, direction: 1 | -1, at: { x: number; y: number }): void {
  const from = targets.get(cy) ?? cy.zoom();
  const level = stepLevel(from, direction);
  if (level === from) return;
  targets.set(cy, level);
  cy.stop();
  cy.animate({ zoom: { level, renderedPosition: at } }, { duration: 140, easing: 'ease-out', complete: () => targets.delete(cy) });
}

/** Randabstand beim Einpassen: links/rechts für die Zeitangaben an Start und Ende des Zeitstrahls, oben Platz für die Werkzeugleiste. */
const FIT_PAD = { x: 150, y: 100 };

/**
 * Fläche, für die beim Öffnen eingepasst wird. Auf kleineren Bildschirmen (Laptop) wird so eingepasst, als wäre die Fläche so groß:
 * Karten, Texte und Bilder erscheinen dann etwa so groß wie auf einem großen Bildschirm statt auf die Hälfte zu schrumpfen; die
 * Ansicht zeigt die Mitte, den Rest erreicht man durch Verschieben oder Herauszoomen. „Alles zeigen“ passt dagegen wirklich alles ein.
 */
export const FIT_AREA = { w: 1600, h: 1000 } as const;

/** Wie eingepasst wird: `view` für die Größe beim Öffnen (siehe FIT_AREA), `all` zeigt alles. */
export type FitMode = 'view' | 'all';

const areaOf = (w: number, h: number, mode: FitMode): { w: number; h: number } => (mode === 'all' ? { w, h } : { w: Math.max(w, FIT_AREA.w), h: Math.max(h, FIT_AREA.h) });

/** Element mit fester Größe auf dem Bildschirm (z. B. Vorschaukarte): Anker im Modellraum, Versatz und Größe in Pixeln. */
export interface ScreenItem {
  ax: number;
  ay: number;
  dx: number;
  dy: number;
  w: number;
  h: number;
}

/** Randabstand beim Einpassen der Handlung: links/rechts Zeitangaben und Schnellauswahl, oben/unten wenig (die Leiste steht links). */
export const STORY_FIT_PAD = { x: 150, y: 36 };

/**
 * Stufe und Verschiebung zum Einpassen, wenn neben dem Modellraum (`world`, wächst mit dem Zoom) Elemente fester Bildschirmgröße
 * zu sehen sein sollen (`items`, z. B. Vorschaukarten über den Marken). Gewählt wird die größte Stufe, bei der alles in die Fläche
 * passt. Passt es nie ganz (feste Elemente schrumpfen nicht mit), die größte, bei der die Breite passt; das Ganze steht dann mittig.
 * `area` ist die Fläche, in die es passen soll (Vorgabe: die Fläche `w`×`h` selbst; größer, siehe FIT_AREA).
 */
export function fitLevel(
  world: { x1: number; y1: number; x2: number; y2: number } | null,
  items: ScreenItem[],
  w: number,
  h: number,
  pad: { x: number; y: number },
  area: { w: number; h: number } = { w, h },
): { zoom: number; pan: { x: number; y: number } } {
  const box = (z: number) => {
    let x1 = Infinity;
    let y1 = Infinity;
    let x2 = -Infinity;
    let y2 = -Infinity;
    if (world) [x1, y1, x2, y2] = [world.x1 * z, world.y1 * z, world.x2 * z, world.y2 * z];
    for (const it of items) {
      const cx = it.ax * z + it.dx;
      const cy = it.ay * z + it.dy;
      x1 = Math.min(x1, cx - it.w / 2);
      x2 = Math.max(x2, cx + it.w / 2);
      y1 = Math.min(y1, cy - it.h / 2);
      y2 = Math.max(y2, cy + it.h / 2);
    }
    return { x1, y1, x2, y2 };
  };
  const levels = [...ZOOM_LEVELS].reverse();
  const fitsW = (b: ReturnType<typeof box>): boolean => b.x2 - b.x1 <= area.w - 2 * pad.x;
  const fitsH = (b: ReturnType<typeof box>): boolean => b.y2 - b.y1 <= area.h - 2 * pad.y;
  const zoom = levels.find((z) => fitsW(box(z)) && fitsH(box(z))) ?? levels.find((z) => fitsW(box(z))) ?? MIN_ZOOM;
  const b = box(zoom);
  if (!Number.isFinite(b.x1)) return { zoom, pan: { x: w / 2, y: h / 2 } };
  return { zoom, pan: { x: w / 2 - (b.x1 + b.x2) / 2, y: h / 2 - (b.y1 + b.y2) / 2 } };
}

/** Einpassen mit Elementen fester Bildschirmgröße (siehe `fitLevel`). */
export function fitViewWithItems(cy: cytoscape.Core, world: cytoscape.NodeCollection, items: ScreenItem[], mode: FitMode = 'view', pad: { x: number; y: number } = STORY_FIT_PAD): void {
  cy.stop();
  targets.delete(cy);
  const w = cy.width();
  const h = cy.height();
  if (w === 0 || h === 0) return;
  const bb = world.nonempty() ? world.boundingBox({ includeLabels: false, includeOverlays: false }) : null;
  cy.viewport(fitLevel(bb, items, w, h, pad, areaOf(w, h, mode)));
}

/**
 * Passt die Ansicht so ein, dass alles mittig zu sehen ist – bei `all` ganz, bei `view` in der Größe von FIT_AREA. Der Zoom ist dabei
 * immer eine der festen Stufen: die größte, bei der alles hineinpasst (die kleinste, wenn selbst die nicht reicht).
 */
export function fitView(cy: cytoscape.Core, elements: cytoscape.NodeCollection = cy.nodes(), mode: FitMode = 'view'): void {
  cy.stop();
  targets.delete(cy);
  const w = cy.width();
  const h = cy.height();
  if (elements.empty() || w === 0 || h === 0) {
    const level = nearestLevel(1);
    cy.viewport({ zoom: level, pan: { x: w / 2, y: h / 2 } });
    return;
  }
  const bb = elements.boundingBox({ includeLabels: false, includeOverlays: false });
  const area = areaOf(w, h, mode);
  const need = Math.min((area.w - 2 * FIT_PAD.x) / Math.max(bb.w, 1), (area.h - 2 * FIT_PAD.y) / Math.max(bb.h, 1));
  const level = levelFitting(need > 0 ? need : MIN_ZOOM);
  cy.viewport({ zoom: level, pan: { x: w / 2 - (level * (bb.x1 + bb.x2)) / 2, y: h / 2 - (level * (bb.y1 + bb.y2)) / 2 } });
}
